import styled from 'styled-components';
import { TotalText } from '.';
import Typography from 'components/Typography/Typography';
import { useTranslation } from 'react-i18next';
import styledTheme from 'common/theme';
import {
  OrderCalculationResponse,
  OrderInputWithSFFlag,
  OrderInputWithPriceInfo
} from 'types/parts';
import { useGetDraftOrderCalculationMutation } from 'api';
import { useEffect, useState } from 'react';
import { ToastMsg } from 'common/components/Toast';
import { Loader } from 'components';
import { CartPriceType } from 'components/CartWizard/helper';
import { cloneDeep } from 'lodash';

const Price = styled.div`
  width: 20rem;
  margin-left: auto;
  color: ${styledTheme.colors.gray800};
  .fast-shipping {
    display: flex;
    align-items: center;
    gap: 0.2rem;
    position: relative;
  }
  .icon--information-circle {
    position: absolute;
    left: -1.2rem;
  }
  margin-top: 2rem;
`;

const Divider = styled.div`
  border-top: 1px solid ${styledTheme.colors.gray600};
`;
interface CartPriceCalculationProps {
  orderInputData?: OrderInputWithSFFlag;
  setOrderInputData?: (data: OrderInputWithSFFlag | undefined) => void;
  setOrderPriceInfo?: ({
    pricedInfo,
    nonePricedInfo
  }: {
    pricedInfo?: OrderInputWithPriceInfo;
    nonePricedInfo?: OrderInputWithPriceInfo;
  }) => void;
  setPriceCalculationLoading?: (loading: boolean) => void;
  selectedPriceType?: CartPriceType;
  cartPriceType?: CartPriceType;
}
const currenciesByCode: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };
const CartPriceCalculation = ({
  orderInputData,
  setPriceCalculationLoading,
  setOrderInputData,
  setOrderPriceInfo,
  selectedPriceType,
  cartPriceType
}: CartPriceCalculationProps): JSX.Element => {
  const { t } = useTranslation(['fpns']);
  const [totalDiscount, setTotalDiscount] = useState<number>(0);
  const [subtotalExTax, setSubtotalExTax] = useState<number>(0);
  const [taxAmount, setTaxAmount] = useState<number>(0);
  const [isLoadingPriceCalculation, setIsLoadingPriceCalculation] = useState<boolean>(false);
  const [getDraftOrderCalculation] = useGetDraftOrderCalculationMutation();
  const [currencyCode, setCurrencyCode] = useState<string>('USD');
  const [draftOrderQuoteInfo, setDraftOrderQuoteInfo] = useState<{
    priced?: OrderInputWithPriceInfo;
    nonePriced?: OrderInputWithPriceInfo;
  }>();

  useEffect(() => {
    if (draftOrderQuoteInfo) {
      const { priced, nonePriced } = draftOrderQuoteInfo;
      // call setOrderPriceInfo only if priced and nonePriced info are not undefined to avoid multiple calls when both priced and non-priced calculation are triggered in mix mode
      if (priced && nonePriced) {
        setOrderPriceInfo?.({ pricedInfo: priced, nonePricedInfo: nonePriced });
      }
    }
  }, [draftOrderQuoteInfo]);
  const processPriceCalculation = (isPriced: boolean, res: OrderCalculationResponse) => {
    // add up total discount from all parts
    let orderPriceInfo: OrderInputWithPriceInfo = {
      subtotal: 0,
      discount: 0,
      tax: 0,
      total: 0,
      extraFee: 0,
      legalizationCost: 0,
      shipping: 0,
      isTwoWayOrder: true,
      erpOrderNumber: res.model?.prosealOrderNumber,
      parts: res.model?.parts,
      currencyCode: res.model?.currencyCode || 'USD'
    };
    if (isPriced) {
      const totalDiscount = res.model?.parts
        ? res.model.parts.reduce((accumulator: number, part) => {
            return accumulator + part.discountAmount;
          }, 0)
        : 0;
      setTotalDiscount(totalDiscount);
      setSubtotalExTax(res.model?.salesPriceExTax ? res.model.salesPriceExTax : 0);
      setTaxAmount(res.model?.salesPriceTaxAmount ? res.model.salesPriceTaxAmount : 0);
      setCurrencyCode(res.model?.currencyCode ? res.model.currencyCode : 'USD');
      //instantiate order price info object

      orderPriceInfo = {
        subtotal: res.model?.salesPriceExTax,
        discount: totalDiscount,
        tax: res.model?.salesPriceTaxAmount,
        total: res.model
          ? res.model?.salesPriceExTax! + res.model?.salesPriceTaxAmount! - totalDiscount
          : 0,
        extraFee: 0,
        legalizationCost: 0,
        shipping: 0,
        isTwoWayOrder: true,
        erpOrderNumber: res.model?.prosealOrderNumber,
        parts: res.model?.parts,
        currencyCode: res.model?.currencyCode || 'USD'
      };
    }
    //send order price info to parent component
    if (selectedPriceType !== 'mix') {
      isPriced
        ? setOrderPriceInfo?.({ pricedInfo: orderPriceInfo })
        : setOrderPriceInfo?.({ nonePricedInfo: orderPriceInfo });
    } else {
      isPriced
        ? setDraftOrderQuoteInfo?.((prev) => ({ ...prev, priced: orderPriceInfo }))
        : setDraftOrderQuoteInfo?.((prev) => ({ ...prev, nonePriced: orderPriceInfo }));
    }
  };
  useEffect(() => {
    if (orderInputData) {
      setIsLoadingPriceCalculation(true);
      setPriceCalculationLoading?.(true);
      let data = cloneDeep(orderInputData);
      const cartItemsPriceType = selectedPriceType === 'nill' ? cartPriceType : selectedPriceType;
      const dataNonePriced =
        selectedPriceType &&
        (selectedPriceType === 'none-price' ||
          selectedPriceType === 'mix' ||
          cartItemsPriceType === 'none-price')
          ? { ...data, orders: [] }
          : undefined;

      const dataPriced =
        selectedPriceType &&
        (selectedPriceType === 'price' ||
          selectedPriceType === 'mix' ||
          cartItemsPriceType === 'price')
          ? { ...data, unpurchasableItems: [] }
          : undefined;
      dataNonePriced &&
        getDraftOrderCalculation(dataNonePriced)
          .unwrap()
          .then((orderResponse) => {
            setIsLoadingPriceCalculation(false);
            setPriceCalculationLoading?.(false);
            if (orderResponse && orderResponse.model) {
              processPriceCalculation(false, orderResponse);
            }
            setOrderInputData?.(undefined);
          })
          .catch((err) => {
            console.error('Error fetching draft order calculation:', err);
            ToastMsg({
              heading: 'Error',
              type: 'error',
              message: 'Unable to get order quote calculation!'
            });
          });
      dataPriced &&
        getDraftOrderCalculation(dataPriced)
          .unwrap()
          .then((orderResponse) => {
            setIsLoadingPriceCalculation(false);
            setPriceCalculationLoading?.(false);
            if (orderResponse && orderResponse.model) {
              processPriceCalculation(true, orderResponse);
            }
            setOrderInputData?.(undefined);
          })
          .catch((err) => {
            console.error('Error fetching draft order calculation:', err);
            ToastMsg({
              heading: 'Error',
              type: 'error',
              message: 'Unable to get order quote calculation!'
            });
          });
    }
  }, [orderInputData, getDraftOrderCalculation, selectedPriceType]);
  return (
    <Price>
      {isLoadingPriceCalculation ? (
        <Loader size={120} />
      ) : (
        <>
          {subtotalExTax > 0 && (
            <TotalText justifyContent="space-between" marginTop="0.3rem">
              <Typography weight="medium" mb={0} size={'.9rem'}>
                {t('machine_subtotal')}:{' '}
              </Typography>
              <Typography weight="medium" mb={0} size={'.9rem'}>
                {currenciesByCode[currencyCode]} {subtotalExTax.toFixed(2)}
              </Typography>
            </TotalText>
          )}

          {totalDiscount > 0 && (
            <TotalText justifyContent="space-between" marginTop="0.3rem">
              <Typography weight="medium" mb={0} size={'.9rem'} color={''}>
                {t('Discount')}:{' '}
              </Typography>
              <Typography weight="medium" mb={0} size={'.9rem'}>
                {currenciesByCode[currencyCode]} {`- ${totalDiscount.toFixed(2)}`}
              </Typography>
            </TotalText>
          )}
          {
            // tempporarily disable shipping calculation
            // <TotalText justifyContent="space-between" marginTop="0.3rem">
            // <Typography weight="medium" mb={0} size={'.9rem'} color={''}>
            //   {t('Shipping')}:{' '}
            // </Typography>
            // <Typography weight="medium" mb={0} size={'.9rem'}>
            //   {'N/A'}
            // </Typography>
            // </TotalText>
          }

          {/* <TotalText justifyContent="space-between" marginTop="0.3rem">
            <div className="fast-shipping">
              <InformationCircle />
              <Typography weight="medium" mb={0} size={'.9rem'} color={''}>
                {t('Extra Fee')}
              </Typography>
              (
              <Typography mb={0} color={styledTheme.colors.gray600}>
                Fast Shipping
              </Typography>
              )
            </div>
            <Typography weight="medium" mb={0} size={'.9rem'}>
              {'N/A'}
            </Typography>
          </TotalText> */}

          {/* <TotalText justifyContent="space-between" marginTop="0.3rem">
            <Typography weight="medium" mb={0} size={'.9rem'} color={''}>
              {t('Legalization Cost')}:{' '}
            </Typography>
            <Typography weight="medium" mb={0} size={'.9rem'}>
              {'N/A'}
            </Typography>
          </TotalText> */}

          {taxAmount > 0 && (
            <TotalText justifyContent="space-between" marginTop="0.3rem">
              <Typography weight="medium" mb={0} size={'.9rem'} color={''}>
                {t('Tax')}:{' '}
              </Typography>
              <Typography weight="medium" mb={0} size={'.9rem'}>
                {currenciesByCode[currencyCode]} {taxAmount ? ` ${taxAmount.toFixed(2)}` : 'N/A'}
              </Typography>
            </TotalText>
          )}

          {subtotalExTax > 0 && (
            <>
              <Divider></Divider>
              <TotalText justifyContent="space-between" marginTop="0.3rem">
                <Typography weight="medium" mb={0} size={'.9rem'} color={''}>
                  {t('Total')}:{' '}
                </Typography>
                <Typography weight="medium" mb={0} size={'.9rem'}>
                  {currenciesByCode[currencyCode]}{' '}
                  {(subtotalExTax + taxAmount - totalDiscount).toFixed(2)}
                </Typography>
              </TotalText>
            </>
          )}
        </>
      )}
    </Price>
  );
};

export default CartPriceCalculation;
